import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import HandleChange from '../../../../../Helpers/HandleChange';
import routes from '../../../../../Routes/routes';
import { permissionObj } from '../../../../../Helpers/permission';
import { FOODIE_SHIFT_MODEL, STEPS_CREATE_FOODIE_SHIFT } from '../Model/Models';
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Company/Payroll/Model/Model";
import {SUB_VIEW_MODEL, TABS_FOODIE} from "../../Model/Models";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Shared hook for foodie shift create/update flows.
 *
 * Mirrors the attendance CommonShiftAccessApp: centralizes form state, the local
 * client-side selection lists (cafeterias / departments / users), their
 * "select for delete" buffers, step navigation and validation. Consumed by both
 * CreateShiftApp (create) and UpdateShiftApp (update).
 *
 * @returns {Object} Shared state and handlers.
 */
const CommonShiftApp = () => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { state } = location;

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [item, setItem] = useState({ ...FOODIE_SHIFT_MODEL });
    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);

    // Client-side selection lists + their delete buffers (one per section).
    const [cafeterias, setCafeterias] = useState([]);
    const [selectDeleteCafeterias, setSelectDeleteCafeterias] = useState([]);

    const [departments, setDepartments] = useState([]);
    const [selectDeleteDepartments, setSelectDeleteDepartments] = useState([]);

    const [user, setUser] = useState([]);
    const [selectDeleteUser, setSelectDeleteUser] = useState([]);

    // Stepper state (create flow).
    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(TABS_FOODIE.SCHEDULES.INDEX);
    const [searchElement, setSearchElement] = useState('');
    const [modified, setModified] = useState(false);

    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /**
     * Validates the view based on permissions; if not authorized, redirects to
     * the forbidden page.
     */
    useEffect(() => {
        if (DEV) return;
        if ((!id && !permission?.includes(permissionObj?.WEB_FOODIE_SHIFT_CREATE)) ||
            (id && !permission?.includes(permissionObj?.WEB_FOODIE_SHIFT_UPDATE))) {
            navigate(routes.forbidden);
        }
    }, [DEV, permission, id]);

    /**
     * Sets the active stepper step (create flow).
     * @param {number} step - Step index.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
        setSearchElement("");
    }

    /**
     * Final validation before create/update.
     * @returns {boolean}
     */
    const validate = () => {
        if (!item.isValid(cafeterias)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }
        setEmptyFields(false);
        return true;
    }

    /**
     * Per-step validation used by the create stepper "next" buttons.
     * @param {number} step - Active step index.
     * @returns {boolean}
     */
    const handleValidateSteps = (step) => {
        if (step === STEPS_CREATE_FOODIE_SHIFT.DINERS.INDEX && !item.isValid(cafeterias)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return false;
        }
        setSearchElement("");
        setEmptyFields(false);
        return true;
    }

    /**
     * Clears only the resources owned by a given step.
     * @param {number} step - Step index.
     */
    const handleClearStep = (step) => {
        const clearMap = {
            [STEPS_CREATE_FOODIE_SHIFT.DINERS.INDEX]: () => {
                setItem(prev => ({ ...prev, name: '' }));
                setSearchElement('');
                setCafeterias([]);
                setModified(true);
            },
            [STEPS_CREATE_FOODIE_SHIFT.DEPARTMENTS.INDEX]: () => {
                setSearchElement('');
                setDepartments([]);
                setModified(true);
            },
            [STEPS_CREATE_FOODIE_SHIFT.CUSTOM_ACCESS.INDEX]: () => {
                setSearchElement('');
                setUser([]);
                setModified(true);
            },
        };
        clearMap[step]?.();
    }

    /**
     * Navigates back to the attendance list on the Shift Access subview.
     */
    const handleOnBack = () => {
        navigate(routes.foodie.list, {
            state: {
                currentTab: TABS_FOODIE.SCHEDULES.INDEX,
                currentSubView: SUB_VIEW_MODEL.SCHEDULE.SHIFTS
            }
        });
    }

    return {
        id,
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        state,

        item, setItem,
        disabled, setDisabled,
        emptyFields, setEmptyFields,
        handleChange,

        cafeterias, setCafeterias,
        selectDeleteCafeterias, setSelectDeleteCafeterias,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,

        steps, setSteps,
        activeStep, setActiveStep,
        searchElement, setSearchElement,
        modified, setModified,
        handleStepChange,

        validate,
        handleValidateSteps,
        handleClearStep,
        handleOnBack,
    }
}

export default CommonShiftApp
